import {
  API_BASE,
  AuthRefreshError,
  hasRefreshToken,
  refreshAuthSession,
} from '@/shared/lib/auth-session'

export { API_BASE }

interface FetchOptions extends RequestInit {
  token?: string
  skipAuthRefresh?: boolean
  retryAfterRefresh?: boolean
}

export class ApiError extends Error {
  status: number

  constructor(status: number, message: string) {
    super(message)
    this.status = status
    this.name = 'ApiError'
  }
}

async function parseJsonResponse<T>(response: Response): Promise<T> {
  const text = await response.text()
  if (text.trim() === '') {
    return undefined as T
  }

  return JSON.parse(text) as T
}

export async function apiClient<T = unknown>(
  endpoint: string,
  options: FetchOptions = {}
): Promise<T> {
  const { token, skipAuthRefresh, retryAfterRefresh, ...fetchOptions } = options

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((fetchOptions.headers as Record<string, string>) || {}),
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const path = endpoint.startsWith('/') ? `/v2${endpoint}` : `/v2/${endpoint}`

  const response = await fetch(`${API_BASE}${path}`, {
    ...fetchOptions,
    headers,
    credentials: 'include',
  })

  if (response.status === 401 && !skipAuthRefresh && !retryAfterRefresh && hasRefreshToken()) {
    try {
      await refreshAuthSession()
    } catch (error) {
      if (error instanceof AuthRefreshError) {
        throw new ApiError(error.status, error.message)
      }
      throw error
    }
    return apiClient<T>(endpoint, { ...options, retryAfterRefresh: true })
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new ApiError(response.status, error.error || error.message || `HTTP ${response.status}`)
  }

  return parseJsonResponse<T>(response)
}
